import React from "react";
import styled from "styled-components";
import { useMutation, queryCache } from "react-query";
import { Button, Row, Col, Popconfirm, Empty } from "antd";
import { EyeOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";

import CustomLink from "components/shared/CustomLink";
import * as ROUTES from "constants/routes";
import { apiAxios } from "services/HttpServices";
import { NotificationStateContext } from "context/UI/Notification/NotificationState";
import useJobDatelineTemplateData from "hooks/api/job/dateline/template/useJobDatelineTemplateData";
import Link from "components/shared/CustomLink";
import { TextInputSkeleton } from "lib/loader/shared/textInputSkeleton";

const deleteTemplate = async ({ datelineTemplateId }) => {
  try {
    const res = await apiAxios.delete(
      `/jobs/dateline-template/${datelineTemplateId}`
    );
    return res.status;
  } catch (e) {
    throw e.response.data.message;
  }
};

const DatelineTemplateList = (props) => {
  const { status, data } = useJobDatelineTemplateData();
  const [error, setError] = React.useState(null);
  const [showIndexDetail, setShowIndexDetail] = React.useState(null);
  // Global State
  const {
    state: notificationState,
    setSuccessMessage,
    clearSuccessMessage,
    setLoading,
    finishLoading,
  } = React.useContext(NotificationStateContext);

  const [mutateDelete] = useMutation(deleteTemplate, {
    onSuccess: (_, variables) => {
      queryCache.setQueryData(["dateline-template"], function (oldData) {
        const newData = oldData.filter(
          (data) => data.id !== variables.datelineTemplateId
        );
        return newData;
      });
    },
    onError: (e) => {
      setError(e);
    },
  });

  const showTaskDetail = (datelineTemplateId) => {
    setShowIndexDetail(datelineTemplateId);
  };

  const cancelDeleteDatelineTemplate = (e) => {
    console.log("e :>> ", e.target.value);
  };

  const deleteDatelineTemplate = async (datelineTemplateId) => {
    setLoading(`deleteDatelineTemplate-${datelineTemplateId}`);
    try {
      await mutateDelete({ datelineTemplateId });
      finishLoading(`deleteDatelineTemplate-${datelineTemplateId}`);
      setSuccessMessage("Succesfully deleted dateline template");
      clearSuccessMessage();
    } catch (error) {
      finishLoading(`deleteDatelineTemplate-${datelineTemplateId}`);
      console.log(error);
    }
  };

  if (
    (status !== "loading" || status !== "idle") &&
    status === "success" &&
    data.length === 0
  ) {
    return (
      <EmptyContainer>
        <Empty
          image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
          imageStyle={{
            height: 60,
          }}
          description={<span>No dateline template</span>}
        >
          <CustomLink href={ROUTES.DATELINE_TEMPLATE_ADD}>
            <Button type="primary">Create Now</Button>
          </CustomLink>
        </Empty>
      </EmptyContainer>
    );
  }

  return (
    <>
      <Row>
        {status === "loading" ? (
          <>
            <Col xs={24}>
              <ListContainer>
                <StyledList>
                  <TextInputSkeleton />
                </StyledList>
              </ListContainer>
            </Col>
            <Col xs={24}>
              <ListContainer>
                <StyledList>
                  <TextInputSkeleton />
                </StyledList>
              </ListContainer>
            </Col>
            <Col xs={24}>
              <ListContainer>
                <StyledList>
                  <TextInputSkeleton />
                </StyledList>
              </ListContainer>
            </Col>
          </>
        ) : (
          <>
            {data.map((template) => (
              <Col xs={24} key={template.id}>
                <ListContainer>
                  <StyledList>
                    <div className="left-content">
                      <h1>{template.title}</h1>
                      <div className="extra">
                        <p className="code">{template.code.toUpperCase()}</p>
                      </div>
                    </div>
                    <div className="right-content">
                      <div className="task">
                        <p>TASK COUNT</p>
                        <h2>{template.taskCount}</h2>
                      </div>
                      <div className="action">
                        <div className="label">
                          <p>ACTIONS</p>
                        </div>
                        <div className="button">
                          <div className="button-container">
                            <Button
                              type="primary"
                              icon={<EyeOutlined />}
                              size="medium"
                              onClick={() => showTaskDetail(template.id)}
                            />
                          </div>
                          {props.selectable ? (
                            <div className="button-container">
                              <Button
                                size="medium"
                                onClick={() =>
                                  props.handleSelectDatelineTemplate(
                                    template.id
                                  )
                                }
                              >
                                SELECT
                              </Button>
                            </div>
                          ) : (
                            <>
                              <Link
                                href={`/jobs/dateline/template/[id]/update`}
                                as={`/jobs/dateline/template/${template.id}/update`}
                              >
                                <div className="button-container">
                                  <Button
                                    icon={<EditOutlined />}
                                    size="medium"
                                  />
                                </div>
                              </Link>
                              <div className="button-container">
                                <Popconfirm
                                  title="Are you sure delete this todo template?"
                                  onConfirm={() =>
                                    deleteDatelineTemplate(template.id)
                                  }
                                  onCancel={cancelDeleteDatelineTemplate}
                                  okText="Yes"
                                  cancelText="No"
                                >
                                  <Button
                                    type="primary"
                                    danger
                                    icon={<DeleteOutlined />}
                                    size="medium"
                                    loading={
                                      notificationState.loading[
                                        `deleteDatelineTemplate-${template.id}`
                                      ]
                                    }
                                  />
                                </Popconfirm>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </StyledList>
                  {showIndexDetail === template.id && (
                    <TaskContainer>
                      {template.template.map((task, i) => (
                        <div className="task-row" key={`datelineTask-${i}`}>
                          <div className="left-content">
                            <p className="title">{task.title}</p>
                          </div>
                          {task.due_after && (
                            <div className="right-content">
                              <p className="detail">{task.due_after}</p>
                              <span className="unit">Days</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </TaskContainer>
                  )}
                </ListContainer>
              </Col>
            ))}
          </>
        )}
      </Row>
    </>
  );
};

const TaskContainer = styled.div`
  margin-top: ${(props) => props.theme.spacing[2]};
  border: 1px solid ${(props) => props.theme.colors.grey[300]};
  background-color: ${(props) => props.theme.colors.grey[100]};
  padding: ${(props) => props.theme.spacing[2]};

  .task-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: ${(props) => props.theme.spacing[2]};
    margin-bottom: ${(props) => props.theme.spacing[2]};
    border-bottom: 1px solid ${(props) => props.theme.colors.grey[300]};

    .left-content {
      display: flex;
      flex-direction: column;

      .title {
        font-size: ${(props) => props.theme.fontSize.l};
      }

      .description {
        font-size: ${(props) => props.theme.fontSize.xs};
        color: ${(props) => props.theme.colors.grey[600]};
      }
    }

    .right-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      .label {
        font-size: ${(props) => props.theme.fontSize.xs};
        text-transform: uppercase;
        color: ${(props) => props.theme.colors.grey[500]};
      }

      .detail {
        font-size: ${(props) => props.theme.fontSize.m};
      }

      .unit {
        font-size: ${(props) => props.theme.fontSize.xs};
        text-transform: uppercase;
        color: ${(props) => props.theme.colors.grey[500]};
      }
    }

    :last-child {
      border-bottom: none;
      margin-bottom: 0;
      padding-bottom: 0;
    }

    p {
      margin-bottom: 0;
    }
  }
`;

const StyledList = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  h1 {
    margin-bottom: 0;
    font-size: ${(props) => props.theme.fontSize["base"]};
  }

  .left-content {
    display: flex;
    flex-direction: column;

    .extra {
      margin-top: ${(props) => props.theme.spacing[1]};
      display: flex;
      p {
        color: ${(props) => props.theme.colors.grey[600]};
        margin-bottom: 0;
      }

      .code {
        margin-right: ${(props) => props.theme.spacing[2]};
      }
    }
  }

  .right-content {
    display: flex;
    align-items: flex-start;

    p {
      margin-bottom: 0;
      font-size: ${(props) => props.theme.fontSize.xs};
      color: ${(props) => props.theme.colors.grey[500]};
    }

    h2 {
      margin-bottom: 0;
    }

    .task {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }

    .action {
      display: flex;
      flex-direction: column;
      margin-left: ${(props) => props.theme.spacing[2]};

      .label {
        align-self: center;
        margin-bottom: ${(props) => props.theme.spacing[2]};
      }

      .button {
        display: flex;

        .button-container {
          margin-left: ${(props) => props.theme.spacing[1]};
        }
      }
    }
  }
`;

const ListContainer = styled.div`
  background: ${(props) => props.theme.colors.white};
  padding: 12px ${(props) => props.theme.spacing[4]};

  border: 1px solid ${(props) => props.theme.colors.grey[400]};
  margin-bottom: ${(props) => props.theme.spacing[2]};
`;

const EmptyContainer = styled.div`
  height: 30%;
  padding: ${(props) => props.theme.spacing[2]};
  background-color: ${(props) => props.theme.colors.white};
  border: 1px solid ${(props) => props.theme.colors.grey[400]};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default DatelineTemplateList;
